import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
const Login = () => {
  const navigate = useNavigate();
  const {
    login,
    isLoggedIn,
    user
  } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    userType: 'customer' // Default to customer login
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn && user && !redirecting) {
      setRedirecting(true);
      redirectBasedOnUserType(user.User_type);
    }
  }, [isLoggedIn, user, navigate, redirecting]);
  const redirectBasedOnUserType = userType => {
    if (userType === 'admin') {
      navigate('/admin');
    } else if (userType === 'delivery') {
      navigate('/delivery');
    } else {
      navigate('/customer');
    }
  };
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async e => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        // Call login with email and password as separate parameters
        const success = await login(formData.email, formData.password);
        if (success) {
          // Login successful, the useEffect above will handle redirection
          console.log('Login successful');
          // We don't need to redirect here, the useEffect will handle it
        } else {
          // Login failed
          setErrors({
            ...errors,
            general: 'Invalid email or password'
          });
        }
      } catch (error) {
        console.error('Login error:', error);
        setErrors({
          ...errors,
          general: 'An error occurred during login'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  // If already redirecting, show loading
  if (redirecting) {
    return <div className="min-h-screen flex items-center justify-center bg-amber-50" data-id="element-1401">
        <div className="text-center" data-id="element-1402">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800 mx-auto" data-id="element-1403"></div>
          <p className="mt-4 text-amber-800" data-id="element-1404">Redirecting...</p>
        </div>
      </div>;
  }
  return <div className="min-h-screen flex items-center justify-center bg-amber-50 py-12 px-4 sm:px-6 lg:px-8" data-id="element-1405">
      <div className="max-w-md w-full space-y-8" data-id="element-1406">
        <div data-id="element-1407">
          <h1 className="text-center text-3xl font-extrabold text-amber-900" data-id="element-1408">
            Sign in to your account
          </h1>
          <p className="mt-2 text-center text-sm text-amber-800" data-id="element-1409">
            Or{' '}
            <Link to="/register" className="font-medium text-amber-800 hover:text-amber-700" data-id="element-1410">
              create a new account
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-id="element-1411">
          {errors.general && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert" data-id="element-1412">
              <span className="block sm:inline" data-id="element-1413">{errors.general}</span>
            </div>}
          <div className="rounded-md shadow-sm -space-y-px" data-id="element-1414">
            <div data-id="element-1415">
              <label htmlFor="email" className="sr-only" data-id="element-1416">
                Email address
              </label>
              <input id="email" name="email" type="email" autoComplete="email" required className={`appearance-none rounded-none relative block w-full px-3 py-2 border ${errors.email ? 'border-red-300' : 'border-amber-300'} placeholder-amber-500 text-amber-900 rounded-t-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 focus:z-10 sm:text-sm`} placeholder="Email address" value={formData.email} onChange={handleChange} data-id="element-1417" />
              {errors.email && <p className="text-red-500 text-xs mt-1" data-id="element-1418">{errors.email}</p>}
            </div>
            <div data-id="element-1419">
              <label htmlFor="password" className="sr-only" data-id="element-1420">
                Password
              </label>
              <input id="password" name="password" type="password" autoComplete="current-password" required className={`appearance-none rounded-none relative block w-full px-3 py-2 border ${errors.password ? 'border-red-300' : 'border-amber-300'} placeholder-amber-500 text-amber-900 rounded-b-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 focus:z-10 sm:text-sm`} placeholder="Password" value={formData.password} onChange={handleChange} data-id="element-1421" />
              {errors.password && <p className="text-red-500 text-xs mt-1" data-id="element-1422">{errors.password}</p>}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-1423">
            <div className="flex items-center" data-id="element-1424">
              <input id="remember-me" name="rememberMe" type="checkbox" className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-amber-300 rounded" checked={formData.rememberMe} onChange={handleChange} data-id="element-1425" />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-amber-900" data-id="element-1426">
                Remember me
              </label>
            </div>
            <div className="text-sm" data-id="element-1427">
              <a href="#" className="font-medium text-amber-800 hover:text-amber-700" data-id="element-1428">
                Forgot your password?
              </a>
            </div>
          </div>
          {/* User Type Selection */}
          <div className="flex items-center justify-center space-x-4" data-id="element-1429">
            <label className="inline-flex items-center" data-id="element-1430">
              <input type="radio" name="userType" value="customer" checked={formData.userType === 'customer'} onChange={handleChange} className="form-radio h-4 w-4 text-amber-600" data-id="element-1431" />
              <span className="ml-2 text-amber-900" data-id="element-1432">Customer</span>
            </label>
            <label className="inline-flex items-center" data-id="element-1433">
              <input type="radio" name="userType" value="admin" checked={formData.userType === 'admin'} onChange={handleChange} className="form-radio h-4 w-4 text-amber-600" data-id="element-1434" />
              <span className="ml-2 text-amber-900" data-id="element-1435">Admin</span>
            </label>
          </div>
          <div data-id="element-1436">
            <button type="submit" disabled={isSubmitting} className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-amber-800 hover:bg-amber-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500" data-id="element-1437">
              {isSubmitting ? <span className="flex items-center" data-id="element-1438">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1439">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1440"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1441"></path>
                  </svg>
                  Signing in...
                </span> : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export default Login;